import { WarningOutlined } from '@ant-design/icons';
import { Row, Col, Checkbox } from 'antd';
import { Formik, FormikHelpers } from 'formik';
import { Form, SubmitButton, Input, Select, DatePicker } from 'formik-antd';
import moment, { Moment } from 'moment';
import { getWorkspace } from 'pages/LoginPage/thunk';
import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { ModalWarningLeavePage, Rectangle } from 'components';
import { t_equipment_lease_machine, t_machine } from 'types';
import { MachineSchema, MachineSchemaChecked } from '../schema';
import { newEquipment, newMachine } from '../slices';
import { getDataStoreFieldEquipment, getDataStoreFieldMachine } from '../thunk';
import TableAdd from './TableAdd';
import Wrap from './styles';

const { Option } = Select;

type Props = {
  nextStep: () => void;
  checked: boolean;
  setChecked: Dispatch<SetStateAction<boolean>>;
};
type InitialValues = {
  lease_start_date: string;
  lease_end_date: string;
  lease_contract_date: string;
  lease_company: string | undefined;
  lease_shop: string | undefined;
  lease_shop_other: string;
  lease_contract_id: string;
  machine_type: string;
  machine_serial_number: string;
  machine_quantity: number;
  machine_other: string;
};

type Datasource = {
  machine_type?: string;
  machine_serial_number?: string;
  machine_other?: string;
  machine_remark?: string;
};

const MachineRegisterFirstStep: FC<Props> = ({ nextStep, checked, setChecked }) => {
  const dispatch = useDispatch();
  const { currentApplicationId } = useSelector((state: RootState) => state.workSpaces);
  const { datastoreEquipmentFields, equipment, machine } = useSelector((state: RootState) => state.machineequipment);
  const [optionCompany, setOptionCompany] = useState<string>(equipment.lease_company?.display_id || '');
  const [optionShop, setOptionShop] = useState<string>(equipment.lease_shop?.display_id || '');
  const [startDate, setStartDate] = useState<string>(equipment.lease_start_date || '');
  const [endDate, setEndDate] = useState<string>(equipment.lease_end_date || '');
  const [leaseDate, setLeaseDate] = useState<string>(equipment.lease_contract_date || '');
  const [leaseId, setLeaseId] = useState<string>(equipment.lease_contract_id || '');
  const [data, setData] = useState<Datasource[]>(machine || []);

  const initialValues: InitialValues = {
    lease_start_date: equipment.lease_start_date || '',
    lease_end_date: equipment.lease_end_date || '',
    lease_contract_date: equipment.lease_contract_date || '',
    lease_company: optionCompany,
    lease_shop: optionShop,
    lease_shop_other: equipment.lease_shop_other || '',
    lease_contract_id: equipment.lease_contract_id || '',
    machine_type: '',
    machine_serial_number: '',
    machine_other: '',
    machine_quantity: equipment.machine_quantity || 0,
  };
  useEffect(() => {
    dispatch(getWorkspace());
  }, [dispatch]);

  useEffect(() => {
    currentApplicationId &&
      dispatch(getDataStoreFieldMachine({ appId: currentApplicationId, dataStoreId: 't_machine' }));
  }, [dispatch, currentApplicationId]);

  useEffect(() => {
    currentApplicationId &&
      dispatch(getDataStoreFieldEquipment({ appId: currentApplicationId, dataStoreId: 't_equipment_lease' }));
  }, [dispatch, currentApplicationId]);

  const optionCompanyLease = useMemo(() => {
    return (
      datastoreEquipmentFields?.find(([_key, value]: [string, any]) => value.display_id === 'lease_company')[1]
        .options || []
    );
  }, [datastoreEquipmentFields]);

  const optionShopLease = useMemo(() => {
    return (
      datastoreEquipmentFields?.find(([_key, value]: [string, any]) => value.display_id === 'lease_shop')[1].options ||
      []
    );
  }, [datastoreEquipmentFields]);

  const onChangeStartDate = (value: any, dateString: string) => {
    setStartDate(dateString);
  };

  const validateStartDate = (dateString: Moment) => {
    return dateString && dateString > moment(endDate).startOf('day');
  };

  const validateEndDate = (dateString: Moment) => {
    return dateString && dateString < moment(startDate).endOf('day');
  };

  const onChangeEndDate = (value: any, dateString: string) => {
    setEndDate(dateString);
  };

  const onChangeLeaseDate = (value: any, dateString: string) => {
    setLeaseDate(dateString);
  };

  const handleSubmit = async (values: any, form: FormikHelpers<InitialValues>) => {
    const machineRegister: t_machine[] = data;
    const equipmentRegister: t_equipment_lease_machine = {
      lease_start_date: startDate,
      lease_end_date: endDate,
      lease_contract_date: leaseDate,
      lease_company: optionCompanyLease.find((item: any) => item.display_id === optionCompany),
      machine_quantity: data.length,
      lease_shop: optionShopLease.find((item: any) => item.display_id === optionShop),
      lease_shop_other: values.lease_shop_other,
      lease_contract_id: leaseId,
    };
    await dispatch(newEquipment(equipmentRegister));
    await dispatch(newMachine(machineRegister));
    form.setSubmitting(false);
    nextStep();
  };

  return (
    <Wrap>
      <span className="smallBoldFont ※">※</span>
      <span className="standardFont">は必須項目</span>
      <Rectangle text="リース情報" />
      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={!checked ? MachineSchema : MachineSchemaChecked}
        render={({ isValid }) => (
          <Form autoComplete={`${Math.random()}`}>
            <div className="rental-detail">
              <div className="input-rental-detail">
                <Form.Item name="check-box">
                  <div className="checked-box">
                    <Checkbox onChange={(e) => setChecked(e.target.checked)} />
                    <span className="title-check-box">購入機器 </span>
                  </div>
                </Form.Item>
                <Row className="dflex">
                  <Col className="dflex-col" span={5}>
                    <span className="standardBoldFont">リース期間</span>
                    {!checked && <span className="smallBoldFont ※">※</span>}
                  </Col>
                  <Col className="dflex baseline" span={19}>
                    <Form.Item name="lease_start_date">
                      <DatePicker
                        onChange={onChangeStartDate}
                        disabledDate={validateStartDate}
                        name="lease_start_date"
                        className="input-width-date"
                        placeholder=""
                        size={'large'}
                        locale={{
                          lang: {
                            locale: 'ja',
                            placeholder: 'Select date',
                            rangePlaceholder: ['Start date', 'End date'],
                            today: 'Today1',
                            now: 'Now',
                            backToToday: 'Back to today',
                            ok: 'Ok',
                            clear: 'Clear',
                            month: 'Month',
                            year: 'Year',
                            timeSelect: 'Select time',
                            dateSelect: 'Select date',
                            monthSelect: 'Choose a month',
                            yearSelect: 'Choose a year',
                            decadeSelect: 'Choose a decade',
                            yearFormat: 'YYYY',
                            dateFormat: 'M/D/YYYY',
                            dayFormat: 'D',
                            dateTimeFormat: 'M/D/YYYY HH:mm:ss',
                            monthFormat: 'MMMM',
                            monthBeforeYear: true,
                            previousMonth: 'Previous month (PageUp)',
                            nextMonth: 'Next month (PageDown)',
                            previousYear: 'Last year (Control + left)',
                            nextYear: 'Next year (Control + right)',
                            previousDecade: 'Last decade',
                            nextDecade: 'Next decade',
                            previousCentury: 'Last century',
                            nextCentury: 'Next century',
                          },
                          timePickerLocale: {
                            placeholder: 'Select time',
                          },
                          dateFormat: 'YYYY-MM-DD',
                          dateTimeFormat: 'YYYY-MM-DD HH:mm:ss',
                          weekFormat: 'YYYY-wo',
                          monthFormat: 'YYYY-MM',
                        }}
                      />
                    </Form.Item>
                    <p className="mg-10 standardBoldFont">〜</p>
                    <Form.Item name="lease_end_date">
                      <DatePicker
                        onChange={onChangeEndDate}
                        disabledDate={validateEndDate}
                        name="lease_end_date"
                        className="input-width-date"
                        placeholder=""
                        size={'large'}
                        locale={{
                          lang: {
                            locale: 'ja',
                            placeholder: 'Select date',
                            rangePlaceholder: ['Start date', 'End date'],
                            today: 'Today1',
                            now: 'Now',
                            backToToday: 'Back to today',
                            ok: 'Ok',
                            clear: 'Clear',
                            month: 'Month',
                            year: 'Year',
                            timeSelect: 'Select time',
                            dateSelect: 'Select date',
                            monthSelect: 'Choose a month',
                            yearSelect: 'Choose a year',
                            decadeSelect: 'Choose a decade',
                            yearFormat: 'YYYY',
                            dateFormat: 'M/D/YYYY',
                            dayFormat: 'D',
                            dateTimeFormat: 'M/D/YYYY HH:mm:ss',
                            monthFormat: 'MMMM',
                            monthBeforeYear: true,
                            previousMonth: 'Previous month (PageUp)',
                            nextMonth: 'Next month (PageDown)',
                            previousYear: 'Last year (Control + left)',
                            nextYear: 'Next year (Control + right)',
                            previousDecade: 'Last decade',
                            nextDecade: 'Next decade',
                            previousCentury: 'Last century',
                            nextCentury: 'Next century',
                          },
                          timePickerLocale: {
                            placeholder: 'Select time',
                          },
                          dateFormat: 'YYYY-MM-DD',
                          dateTimeFormat: 'YYYY-MM-DD HH:mm:ss',
                          weekFormat: 'YYYY-wo',
                          monthFormat: 'YYYY-MM',
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row className="dflex mt-10">
                  <Col className="dflex-col" span={5}>
                    <span className="standardBoldFont">契約日</span>
                    <span className="smallBoldFont ※">※</span>
                  </Col>
                  <Col className="dflex baseline" span={19}>
                    <Form.Item name="lease_contract_date">
                      <DatePicker
                        onChange={onChangeLeaseDate}
                        name="lease_contract_date"
                        className="input-width-date"
                        placeholder=""
                        size={'large'}
                        locale={{
                          lang: {
                            locale: 'ja',
                            placeholder: 'Select date',
                            rangePlaceholder: ['Start date', 'End date'],
                            today: 'Today1',
                            now: 'Now',
                            backToToday: 'Back to today',
                            ok: 'Ok',
                            clear: 'Clear',
                            month: 'Month',
                            year: 'Year',
                            timeSelect: 'Select time',
                            dateSelect: 'Select date',
                            monthSelect: 'Choose a month',
                            yearSelect: 'Choose a year',
                            decadeSelect: 'Choose a decade',
                            yearFormat: 'YYYY',
                            dateFormat: 'M/D/YYYY',
                            dayFormat: 'D',
                            dateTimeFormat: 'M/D/YYYY HH:mm:ss',
                            monthFormat: 'MMMM',
                            monthBeforeYear: true,
                            previousMonth: 'Previous month (PageUp)',
                            nextMonth: 'Next month (PageDown)',
                            previousYear: 'Last year (Control + left)',
                            nextYear: 'Next year (Control + right)',
                            previousDecade: 'Last decade',
                            nextDecade: 'Next decade',
                            previousCentury: 'Last century',
                            nextCentury: 'Next century',
                          },
                          timePickerLocale: {
                            placeholder: 'Select time',
                          },
                          dateFormat: 'YYYY-MM-DD',
                          dateTimeFormat: 'YYYY-MM-DD HH:mm:ss',
                          weekFormat: 'YYYY-wo',
                          monthFormat: 'YYYY-MM',
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row className="dflex mt-20">
                  <Col className="dflex-col" span={5}>
                    <span className="standardBoldFont">リース会社</span>
                    {!checked && <span className="smallBoldFont ※">※</span>}
                  </Col>
                  <Col className="dflex" span={19}>
                    <Form.Item name="lease_company">
                      <Select
                        name="lease_company"
                        value={optionCompany}
                        allowClear
                        onChange={(e) => setOptionCompany(e)}
                        className="input-width-select"
                        size={'large'}
                      >
                        {optionCompanyLease.map((g: any, i: number) => (
                          <Option key={i} value={g.display_id}>
                            {g.value}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row className="dflex mt-20">
                  <Col className="dflex-col" span={5}>
                    <span className="standardBoldFont">販売店</span>
                    <span className="smallBoldFont ※">※</span>
                  </Col>
                  <Col className="dflex" span={19}>
                    <Form.Item name="lease_shop">
                      <Select
                        name="lease_shop"
                        allowClear
                        value={optionShop}
                        onChange={(e) => setOptionShop(e)}
                        className="input-width-select"
                        size={'large'}
                      >
                        {optionShopLease.map((g: any, i: number) => (
                          <Option key={i} value={g.display_id}>
                            {g.value}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item name="lease_shop_other">
                      <Input
                        name="lease_shop_other"
                        className="input-width ml-10"
                        style={{ backgroundColor: optionShop === 'o_other' ? '#fff' : '#dddddd' }}
                        disabled={optionShop === 'o_other' ? false : true}
                        size={'large'}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row className="dflex mt-20">
                  <Col className="dflex-col" span={5}>
                    <span className="standardBoldFont">契約番号</span>
                    <span className="smallBoldFont ※">※</span>
                  </Col>
                  <Col className="dflex" span={19}>
                    <Form.Item name="lease_contract_id">
                      <Input
                        name="lease_contract_id"
                        className="input-width-select"
                        size={'large'}
                        onChange={(e) => setLeaseId(e.target.value)}
                        autoComplete={`${Math.random()}`}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </div>
            <div className="table-add-detail">
              <Rectangle text="リース機器一覧" />
              <TableAdd dataSource={data} setData={setData} />
            </div>
            {!isValid && (
              <div style={{ marginTop: '0.5rem' }} className="error">
                <WarningOutlined />
                <p>入力内容に不備がございます。エラーが表示されている項目をご確認ください。</p>
              </div>
            )}
            <SubmitButton className="btn-confirm-register middleBoldFont">登録内容を確認する</SubmitButton>
          </Form>
        )}
      />
      <ModalWarningLeavePage />
    </Wrap>
  );
};
export default MachineRegisterFirstStep;
